import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { useProjectsQuery, useTeamsQuery } from '../../hooks/queries';
import { teamSuffixClass } from '../layout/SidebarParts';

/**
 * The projects a bot works in, one under the other with a search (people have
 * dozens). Shared by the phone sheet and the desktop picker; `compact` is the
 * denser desktop size.
 *
 * Grouped as the sidebar is: Favorites first, then personal projects, then each team
 * under its header (dot + name). Lists that pick from projects of several teams split
 * them with headers, not a label per row (Copilot's agent store, ChatGPT's agents,
 * Claude's projects); a favorite also stays in its own group, as in the sidebar.
 *
 * noneLabel adds "No project" (onPick(null)). With anyLabel too (a filter, the Chats page's
 * history) the first row is "Any project" (onPick(null), projectId null) and "No project"
 * picks 'none'.
 */
const ProjectList = ({ projects, projectId, onPick, compact = false, autoFocus = false, noneLabel = null, anyLabel = null }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data: allProjects } = useProjectsQuery();
  const { data: teams } = useTeamsQuery();
  const q = query.trim().toLowerCase();

  const groups = useMemo(() => {
    const favorite = new Set((allProjects || []).filter((p) => p.is_favorite).map((p) => p.id));
    const shown = q ? projects.filter((p) => (p.name || '').toLowerCase().includes(q)) : projects;
    const out = [
      { key: 'favorites', label: t('nav.favorites'), items: shown.filter((p) => favorite.has(p.id)) },
      { key: 'personal', label: t('bots.scope_personal'), items: shown.filter((p) => !p.team_id) },
    ];
    (teams || []).forEach((team) => {
      out.push({ key: team.id, label: team.name, color: team.color || '#3B82F6',
                 items: shown.filter((p) => p.team_id === team.id) });
    });
    // A team this person sees projects of but is not listed (should not happen): still shown.
    const listed = new Set((teams || []).map((tm) => tm.id));
    const other = shown.filter((p) => p.team_id && !listed.has(p.team_id));
    if (other.length) out.push({ key: 'other', label: '', items: other });
    return out.filter((g) => g.items.length);
  }, [projects, allProjects, teams, q, t]);

  // Among the favorites a team project names its team after it, as in the sidebar.
  const teamName = (p) => (p.team_id ? (teams || []).find((tm) => tm.id === p.team_id)?.name : null);
  const row = (p, key, withTeam = false) => {
    const on = p.id === projectId;
    const team = withTeam ? teamName(p) : null;
    return (
      <li key={key}>
        <button type="button" onClick={() => onPick(p.id)} aria-pressed={on} data-testid="bot-switcher-project"
          className={`w-full text-left flex items-center gap-3 rounded-lg ${compact ? 'min-h-9 px-2.5 text-sm' : 'min-h-12 px-3 rounded-xl text-[15px]'} ${
            on ? 'bg-accent' : 'hover:bg-accent/60 active:bg-accent/60'}`}>
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#71717a' }} />
          <span className={`flex-1 min-w-0 truncate text-foreground ${on ? 'font-semibold' : ''}`}
            title={[p.name, team].filter(Boolean).join(' · ')}>
            {p.name}
            {team && <span className={teamSuffixClass}> · {team}</span>}
          </span>
          {on && <Check className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2.5} />}
        </button>
      </li>
    );
  };

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="bot-switcher-projects">
      <label className={`flex items-center gap-2 rounded-lg border border-border text-muted-foreground focus-within:border-amber-500/60 ${
        compact ? 'm-2 h-9 px-2.5' : 'mx-4 mb-2 h-11 px-3'}`}>
        <Search className="w-4 h-4 shrink-0" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('projects.search_placeholder')}
          aria-label={t('projects.search_placeholder')} data-testid="bot-switcher-project-search" autoFocus={autoFocus}
          className={`flex-1 min-w-0 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none ${compact ? 'text-sm' : 'text-base'}`} />
      </label>
      <ul className={`flex-1 min-h-0 overflow-y-auto pb-2 ${compact ? 'px-1.5 max-h-80' : 'px-2'}`}>
        {!q && [anyLabel && ['any', anyLabel, null, !projectId], noneLabel && ['none', noneLabel, anyLabel ? 'none' : null,
          anyLabel ? projectId === 'none' : !projectId]].filter(Boolean).map(([key, label, value, on]) => (
          <li key={key}>
            <button type="button" onClick={() => onPick(value)} aria-pressed={on} data-testid={`bot-switcher-${key === 'any' ? 'any' : 'no'}-project`}
              className={`w-full text-left flex items-center gap-3 rounded-lg ${compact ? 'min-h-9 px-2.5 text-sm' : 'min-h-12 px-3 rounded-xl text-[15px]'} ${
                on ? 'bg-accent' : 'hover:bg-accent/60 active:bg-accent/60'}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-muted-foreground/50" />
              <span className={`flex-1 min-w-0 truncate text-muted-foreground ${on ? 'font-semibold' : ''}`}>{label}</span>
              {on && <Check className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2.5} />}
            </button>
          </li>
        ))}
        {groups.length === 0 ? (
          <li className="px-4 py-6 text-sm text-muted-foreground text-center">{t('projects.no_projects')}</li>
        ) : groups.map((g) => (
          <li key={g.key} data-testid="bot-switcher-group">
            {g.label && (
              <div className={`flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground/80 ${compact ? 'px-2.5 pt-2 pb-1' : 'px-3 pt-3 pb-1'}`}>
                {g.color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />}
                <span className="truncate">{g.label}</span>
              </div>
            )}
            <ul>{g.items.map((p) => row(p, `${g.key}-${p.id}`, g.key === 'favorites'))}</ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProjectList;
